import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { BulkPlanDialog } from "@/components/BulkPlanDialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calendar as CalendarIcon,
  CaretLeft,
  CaretRight,
  Plus,
  X as CloseIcon,
  MagnifyingGlass,
  ShoppingCart,
  Printer,
  Copy as CopyIcon,
  Trash,
  Egg,
  ForkKnife,
  Moon,
  Cookie,
  CheckCircle,
  PencilSimple,
  Check,
} from "@phosphor-icons/react";
import { EmailPlanForm } from "@/components/EmailPlanForm";
import { ShareDialog } from "@/components/sharing/ShareDialog";
import { PlannerSwitcher } from "@/components/planner/PlannerSwitcher";

/* /planner — Weekly Meal Planner (Phase 1)
 *
 * Layout:
 *   Header  : brand overline + h1 + week nav (< This week >) + toolbar
 *   Grid    : 7 columns × 4 rows (Breakfast, Lunch, Dinner, Snacks)
 *             desktop: table  |  mobile: stacked "day cards"
 *   Sheet   : recipe picker (opens on tap of any slot)
 *   Footer  : "Add to shopping list" primary CTA (auto-syncs future edits)
 *
 * Interaction model — TAP-TO-PICK on every device (Clove/Mealime pattern).
 * Empty slot: shows "+ Add" — tap opens the picker sheet.
 * Filled slot: shows recipe thumb + title — tap opens edit sheet
 *   (change recipe / adjust servings / write a note / clear the slot).
 *
 * Auto-sync policy (user choice 2026-02-26): once "Add week to Shopping list"
 * is tapped, any subsequent slot change silently mirrors to the shopping list.
 */

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const MEALS = [
  { key: "breakfast", label: "Breakfast", Icon: Egg, tone: "bg-amber-50" },
  { key: "lunch",     label: "Lunch",     Icon: ForkKnife, tone: "bg-lime-50" },
  { key: "dinner",    label: "Dinner",    Icon: Moon, tone: "bg-orange-50" },
  { key: "snacks",    label: "Snacks",    Icon: Cookie, tone: "bg-rose-50" },
];

/** Format a Date object as YYYY-MM-DD using its LOCAL components.
 *
 * IMPORTANT: never use `Date.toISOString()` for calendar dates — it converts
 * to UTC first, so a local midnight in a timezone ahead of UTC (e.g. BST)
 * silently returns YESTERDAY's date. That was the reported bug where the
 * Meal Planner grid showed Saturday under Fri's date (all columns shifted
 * one day earlier).
 */
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Return the Monday of the given date's ISO week, as YYYY-MM-DD (local). */
function mondayOf(input) {
  const d = new Date(input);
  const day = (d.getDay() + 6) % 7; // 0 = Mon, 6 = Sun
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return ymd(d);
}
function addDays(iso, n) {
  // Parse the YYYY-MM-DD as LOCAL midnight (append T00:00:00) so subsequent
  // getDate() / setDate() stay on the intended calendar day regardless of tz.
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return ymd(d);
}
function humanDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function humanRange(mondayIso) {
  const start = new Date(mondayIso + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts = { day: "numeric", month: "short" };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}
/** Today's date as YYYY-MM-DD in LOCAL time — used for the "Today" highlight. */
function todayLocalYmd() {
  return ymd(new Date());
}

export default function MealPlanner() {
  const { flags, loading: flagsLoading } = useSiteFlags();
  const { user } = useAuth();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const [weekStart, setWeekStart] = useState(() => {
    const w = new URLSearchParams(window.location.search).get("week");
    return w && /^\d{4}-\d{2}-\d{2}$/.test(w) ? mondayOf(w + "T00:00:00") : mondayOf(new Date());
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState(null); // {day, meal_type, existing?}
  const [bulkPlan, setBulkPlan] = useState(null); // {recipes, meal} from "Pick several"
  const printRef = useRef(null);
  const [planId, setPlanIdState] = useState(() => new URLSearchParams(window.location.search).get("plan") || localStorage.getItem("rf_planner_id") || "");
  const [planners, setPlanners] = useState({ owned: [], shared: [], paid: false });
  const setPlanId = (id) => { if (id) localStorage.setItem("rf_planner_id", id); else localStorage.removeItem("rf_planner_id"); setPlanIdState(id); };
  const pq = planId ? { plan_id: planId } : {};
  const loadPlanners = useCallback(() => api.get("/meal-planners").then(({ data }) => setPlanners(data)).catch(() => {}), []);
  const [liveShareOpen, setLiveShareOpen] = useState(false);
  const planRole = plan?.role || "owner";

  const loadPlan = useCallback(async (ws, quiet) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/meal-plan", { params: { week_start: ws, ...(planId ? { plan_id: planId } : {}) } });
      setPlan(data);
    } catch {
      if (planId) { setPlanId(""); return; }
      if (!quiet) toast.error("Couldn't load your meal plan");
    } finally {
      setLoading(false);
    }
  }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    loadPlanners();
  }, [user, loadPlanners]);

  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => { if (!document.hidden) loadPlan(weekStart, true); }, 10000);
    return () => clearInterval(id);
  }, [user, weekStart, loadPlan]);

  useEffect(() => {
    // Anonymous visitors can view the /planner marketing preview but the API
    // endpoints are auth-gated. Skip the fetch (no console 401 spam) — the
    // <SignedOutNotice /> below renders in their place.
    if (!user) { setLoading(false); return; }
    loadPlan(weekStart);
  }, [weekStart, loadPlan, user]);

  const slotsByKey = useMemo(() => {
    const map = {};
    (plan?.slots || []).forEach((s) => { map[`${s.day}:${s.meal_type}`] = s; });
    return map;
  }, [plan]);

  // Feature-gate: if the Meal Planner flag is off, only Admin/SuperAdmin
  // can preview the page. Everyone else bounces home. Placed AFTER every
  // hook so React's hook-order invariant stays intact between renders.
  if (!flagsLoading && !flags.meal_planner_enabled && !isStaff) {
    return <Navigate to="/" replace />;
  }

  const filledCount = (plan?.slots || []).filter((s) => !!s.recipe_id).length;
  const anyFilled = (plan?.slots || []).some((s) => s.recipe_id || s.recipe_title || s.note);

  const openPicker = (day, meal_type) => {
    if (!user) {
      toast.info("Sign in to save meals to your planner");
      return;
    }
    setPickerSlot({ day, meal_type, existing: slotsByKey[`${day}:${meal_type}`] || null });
    setPickerOpen(true);
  };

  const upsertSlot = async ({ day, meal_type, recipe_id, servings, note, custom_title, leftover_of }) => {
    try {
      const { data } = await api.put("/meal-plan/slot", {
        week_start: weekStart, day, meal_type, plan_id: planId || undefined,
        recipe_id: recipe_id || null,
        custom_title: custom_title || "",
        servings: servings ?? 4,
        note: note || "",
        leftover_of: leftover_of || "",
      });
      setPlan(data);
      if (data?.synced_to_shopping) {
        toast.success("Slot updated · shopping list re-synced");
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update slot");
      return false;
    }
  };

  const clearSlot = (day, meal_type) => upsertSlot({ day, meal_type, recipe_id: null, servings: 4, note: "" });

  const copyPrevWeek = async () => {
    if (!user) { toast.info("Sign in to plan and copy weekly meals"); return; }
    const prev = addDays(weekStart, -7);
    try {
      const { data } = await api.post("/meal-plan/copy", {
        from_week_start: prev, to_week_start: weekStart, ...pq,
      });
      setPlan(data);
      toast.success("Copied last week's plan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to copy from last week");
    }
  };

  const clearWeek = async () => {
    if (!window.confirm("Clear every meal on this week?")) return;
    try {
      await api.post("/meal-plan/clear", { week_start: weekStart, ...pq });
      toast.success("Week cleared");
      loadPlan(weekStart);
    } catch { toast.error("Couldn't clear the week"); }
  };

  const addWeekToShopping = async () => {
    try {
      const { data } = await api.post("/meal-plan/add-to-shopping-list", { week_start: weekStart, ...pq });
      toast.success(`Added ${data.added} ingredients to your shopping list`);
      loadPlan(weekStart);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sync shopping list");
    }
  };

  const printWeek = () => {
    // Native browser print, aimed at the print-only wrapper. `@media print`
    // CSS in App.css hides the app chrome and shows only #meal-plan-print.
    window.print();
  };

  const shareText = () => {
    const lines = [`My meal plan · ${humanRange(weekStart)}`, ""];
    DAYS.forEach(({ key, label }, i) => {
      const dayDate = humanDate(addDays(weekStart, i));
      lines.push(`${label} ${dayDate}`);
      MEALS.forEach(({ key: m, label: mLabel }) => {
        const s = slotsByKey[`${key}:${m}`];
        if (s && (s.recipe_title || s.note)) {
          lines.push(`  ${mLabel}: ${s.recipe_title || ""}${s.note ? ` (${s.note})` : ""}`);
        }
      });
      lines.push("");
    });
    navigator.clipboard.writeText(lines.join("\n").trim())
      .then(() => toast.success("Copied plan to clipboard"))
      .catch(() => toast.error("Copy failed"));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-8 pb-24">
      <AutoSeo
        pageKey="planner"
        fallbackTitle="Weekly meal planner — Recipe Filer"
        fallbackDescription="Drop your favourite recipes into a weekly grid and auto-build the shopping list. Copy last week, adjust servings, note leftovers, print for the fridge."
      />

      {/* ---- Header ---- */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6" data-testid="planner-header">
        <div>
          <p className="overline">Recipe Filer</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-rf-ink">Weekly meal planner</h1>
          {user && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="planner-share-row">
              <PlannerSwitcher planners={planners} value={planId} onChange={setPlanId} onChanged={loadPlanners} role={planRole} />
              {planRole === "owner" ? (
                <button type="button" onClick={() => setLiveShareOpen(true)} data-testid="planner-live-share-btn" className="btn-outline-ink !py-1.5 !px-3 text-sm">Share planner</button>
              ) : (
                <span className="text-xs font-bold text-rf-ink-soft" data-testid="planner-shared-badge">{planRole === "view" ? "View only" : "Shared planner · changes sync live"}</span>
              )}
            </div>
          )}
          <p className="text-rf-ink-soft mt-1 text-sm sm:text-base">
            Tap any slot to add a recipe. When you&apos;re ready, sync the whole week to your shopping list.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="planner-toolbar">
          <button
            onClick={copyPrevWeek}
            className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"
            data-testid="planner-copy-prev"
          >
            <CopyIcon size={16} /> Copy last week
          </button>
          <button
            onClick={printWeek}
            className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"
            data-testid="planner-print"
          >
            <Printer size={16} /> Print
          </button>
          <button
            onClick={shareText}
            className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"
            data-testid="planner-share"
          >
            <CopyIcon size={16} /> Copy text
          </button>
          <button
            onClick={clearWeek}
            className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2 !border-red-400 !text-red-600"
            data-testid="planner-clear"
          >
            <Trash size={16} /> Clear week
          </button>
        </div>
      </div>

      {/* ---- Sign-in notice (anonymous visitors) ---- */}
      {!user && (
        <div
          className="mb-6 bg-rf-amber/15 border-2 border-rf-amber/40 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3"
          data-testid="planner-signin-notice"
        >
          <div className="flex-1 min-w-[260px]">
            <p className="font-black text-rf-ink text-base">
              👋 The Meal Planner is a members-only tool
            </p>
            <p className="text-sm text-rf-ink-soft mt-1">
              Sign in (or start free) to plan your week, auto-sync every recipe into your shopping list, share the plan with your household, and print a fridge-ready copy. The empty grid below is a preview — nothing you tap will save until you&apos;re signed in.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to="/login?next=/planner" className="btn-outline-ink !py-2 !px-4 text-sm">Sign in</Link>
            <Link to="/register?next=/planner" className="btn-amber !py-2 !px-4 text-sm">Start free</Link>
          </div>
        </div>
      )}

      {/* ---- Week nav ---- */}
      <div className="flex items-center justify-between gap-3 mb-6 bg-white border border-rf-ink/10 rounded-2xl px-3 py-2" data-testid="planner-week-nav">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-1"
          aria-label="Previous week"
          data-testid="planner-prev-week"
        >
          <CaretLeft size={16} weight="bold" /> Prev
        </button>
        <div className="text-center">
          <p className="font-display font-black text-base sm:text-lg text-rf-ink flex items-center gap-2 justify-center whitespace-nowrap">
            <CalendarIcon size={18} weight="duotone" className="text-rf-orange hidden sm:block" />
            {humanRange(weekStart)}
          </p>
          <button
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="text-xs text-rf-orange hover:underline font-bold"
            data-testid="planner-this-week"
          >
            Jump to this week
          </button>
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-1"
          aria-label="Next week"
          data-testid="planner-next-week"
        >
          Next <CaretRight size={16} weight="bold" />
        </button>
      </div>

      {loading && (
        <div className="text-center py-16 text-rf-ink-soft">Loading your week…</div>
      )}

      {/* ---- Desktop grid (md+) ---- */}
      {!loading && (
        <div className="hidden md:block overflow-x-auto" id="meal-plan-print" ref={printRef}>
          <table className="w-full table-fixed border-separate border-spacing-1" data-testid="planner-grid-desktop">
            <thead>
              <tr>
                <th className="w-28"></th>
                {DAYS.map((d, i) => {
                  const isToday = addDays(weekStart, i) === todayLocalYmd();
                  return (
                    <th key={d.key} className={`p-2 rounded-xl ${isToday ? "bg-rf-amber/30" : "bg-rf-cream-alt/60"}`}>
                      <div className="font-display font-black text-rf-ink">{d.label}</div>
                      <div className="text-xs text-rf-ink-soft">{humanDate(addDays(weekStart, i))}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {MEALS.map(({ key: mKey, label, Icon, tone }) => (
                <tr key={mKey}>
                  <td className={`p-3 rounded-xl align-top ${tone}`}>
                    <div className="flex items-center gap-2 font-display font-black text-rf-ink">
                      <Icon size={18} weight="duotone" className="text-rf-orange" />
                      {label}
                    </div>
                  </td>
                  {DAYS.map((d) => {
                    const slot = slotsByKey[`${d.key}:${mKey}`];
                    return (
                      <td key={d.key} className="p-1 align-top">
                        <SlotCell
                          slot={slot}
                          onClick={() => openPicker(d.key, mKey)}
                          testId={`slot-${d.key}-${mKey}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Mobile stacked days ---- */}
      {!loading && (
        <div className="md:hidden space-y-4" data-testid="planner-grid-mobile">
          {DAYS.map((d, i) => {
            const isToday = addDays(weekStart, i) === todayLocalYmd();
            return (
              <div
                key={d.key}
                className={`rounded-2xl p-3 border ${isToday ? "border-rf-orange bg-rf-amber/15" : "border-rf-ink/10 bg-white"}`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-display font-black text-rf-ink text-lg">
                    {d.label} <span className="text-rf-ink-soft text-sm font-normal">{humanDate(addDays(weekStart, i))}</span>
                  </h3>
                  {isToday && <span className="chip !bg-rf-orange !text-white !border-0 text-xs">Today</span>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {MEALS.map(({ key: mKey, label, Icon }) => {
                    const slot = slotsByKey[`${d.key}:${mKey}`];
                    return (
                      <div key={mKey}>
                        <div className="text-xs text-rf-ink-soft mb-1 inline-flex items-center gap-1">
                          <Icon size={12} weight="duotone" className="text-rf-orange" /> {label}
                        </div>
                        <SlotCell
                          slot={slot}
                          onClick={() => openPicker(d.key, mKey)}
                          testId={`slot-${d.key}-${mKey}`}
                          compact
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Footer CTA ---- */}
      {!loading && (
        <div className="sticky bottom-4 mt-8" data-testid="planner-footer">
          <div className="card-recipe !p-3 sm:!p-4 flex flex-wrap items-center justify-between gap-2 sm:gap-3 shadow-lg">
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-rf-amber/25 flex items-center justify-center">
                <ShoppingCart size={22} weight="duotone" className="text-rf-orange" />
              </div>
              <div>
                <p className="font-display font-black text-rf-ink">
                  {filledCount === 0 ? "Add recipes to plan your week" : `${filledCount} meal${filledCount === 1 ? "" : "s"} planned`}
                </p>
                {plan?.synced_to_shopping ? (
                  <p className="text-xs text-green-700 inline-flex items-center gap-1">
                    <CheckCircle size={12} weight="fill" /> Shopping list auto-syncs with your plan
                  </p>
                ) : (
                  <p className="text-xs text-rf-ink-soft">
                    One tap sends every ingredient to your shopping list.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none">
              <Link
                to="/shopping"
                className="btn-outline-ink !py-2 !px-3 text-sm justify-center text-center"
                data-testid="planner-view-shopping"
              >
                View shopping list
              </Link>
              <button
                onClick={addWeekToShopping}
                disabled={filledCount === 0}
                className="btn-orange !py-2 !px-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="planner-add-to-shopping"
              >
                <ShoppingCart size={16} /> {plan?.synced_to_shopping ? "Re-sync" : <><span className="sm:hidden">Add to list</span><span className="hidden sm:inline">Add to shopping list</span></>}
              </button>
            </div>
          </div>
        </div>
      )}
      {bulkPlan && (
        <BulkPlanDialog open recipes={bulkPlan.recipes} initialWeek={weekStart} initialMeal={bulkPlan.meal} planId={planId}
                        onOpenChange={(o) => { if (!o) { setBulkPlan(null); loadPlan(weekStart); } }}
                        onDone={() => { setBulkPlan(null); loadPlan(weekStart); }} />
      )}


      {/* ---- Picker sheet ---- */}
      {pickerOpen && pickerSlot && (
        <RecipePickerSheet
          slot={pickerSlot}
          weekStart={weekStart}
          onMulti={(recipes) => { setBulkPlan({ recipes, meal: pickerSlot.meal_type }); setPickerOpen(false); setPickerSlot(null); }}
          onClose={() => { setPickerOpen(false); setPickerSlot(null); }}
          onSave={async (payload) => {
            const ok = await upsertSlot({ ...pickerSlot, ...payload });
            if (ok) {
              const d = DAYS.find((x) => x.key === pickerSlot.day)?.label || pickerSlot.day;
              const m = MEALS.find((x) => x.key === pickerSlot.meal_type)?.label || pickerSlot.meal_type;
              toast.success(`Meal saved · ${d} ${m.toLowerCase()}`);
            }
            return ok;
          }}
          onClear={async () => {
            await clearSlot(pickerSlot.day, pickerSlot.meal_type);
            setPickerOpen(false); setPickerSlot(null);
          }}
        />
      )}

      <ShareDialog open={liveShareOpen} onOpenChange={setLiveShareOpen} resourceType="meal_plan" resourceId={plan?.planner_id || (user ? `mp_${user.user_id}` : "")} label={plan?.planner_name || "your meal planner"} onChanged={loadPlanners}>
        {planRole === "owner" && (
          <div className="border-t border-rf-ink/10 pt-4" data-testid="share-email-week">
            <p className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft">Email this week · {humanRange(weekStart)}</p>
            {anyFilled ? (
              <>
                <p className="text-sm text-rf-ink-soft mt-1 mb-2">Sends a read-only link to just this week — no sign-in needed. It stops working a week after the week ends.</p>
                <EmailPlanForm weekStart={weekStart} planId={planId} />
              </>
            ) : <p className="text-sm text-rf-ink-soft mt-1" data-testid="share-email-week-empty">Add a recipe to this week to email it.</p>}
          </div>
        )}
      </ShareDialog>
    </div>
  );
}


/* ---------- Slot cell ---------- */

function SlotCell({ slot, onClick, testId, compact = false }) {
  if (!slot || (!slot.recipe_id && !slot.recipe_title && !slot.note)) {
    return (
      <button
        onClick={onClick}
        data-testid={testId}
        className={`w-full ${compact ? "h-16" : "h-24"} rounded-xl border-2 border-dashed border-rf-ink/15 hover:border-rf-orange hover:bg-rf-amber/10 transition text-rf-ink-soft inline-flex flex-col items-center justify-center gap-1`}
      >
        <Plus size={compact ? 16 : 20} />
        <span className="text-xs font-bold">Add</span>
      </button>
    );
  }
  // "Custom / freeform" — user typed a meal that isn't a Recipe Filer recipe.
  const isCustom = !slot.recipe_id && !!slot.recipe_title;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full ${compact ? "h-16" : "h-24"} rounded-xl bg-white border border-rf-ink/10 hover:border-rf-orange hover:shadow transition text-left flex overflow-hidden group`}
    >
      {slot.recipe_image ? (
        <img
          src={slot.recipe_image}
          alt=""
          className={`${compact ? "w-16" : "w-20"} h-full object-cover shrink-0`}
        />
      ) : slot.recipe_id ? (
        <div className={`${compact ? "w-16" : "w-20"} h-full shrink-0 bg-rf-amber/25 flex items-center justify-center`}>
          <ForkKnife size={compact ? 16 : 22} weight="duotone" className="text-rf-orange" />
        </div>
      ) : isCustom ? (
        <div className={`${compact ? "w-16" : "w-20"} h-full shrink-0 bg-rf-cream-alt flex items-center justify-center`}>
          <PencilSimple size={compact ? 16 : 22} weight="duotone" className="text-rf-ink-soft" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0 p-2 flex flex-col">
        <p className={`font-display font-bold text-rf-ink line-clamp-2 ${compact ? "text-xs" : "text-sm"}`}>
          {slot.recipe_title || (slot.note ? "Note" : "")}
        </p>
        {slot.leftover_of && (
          <span className="inline-flex self-start items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-rf-cream-alt text-[9px] font-black uppercase tracking-wider text-rf-ink-soft border border-rf-ink/10">
            Leftovers
          </span>
        )}
        {slot.note && (
          <p className="text-[10px] text-rf-ink-soft italic line-clamp-2 mt-0.5">
            {slot.note}
          </p>
        )}
        {slot.recipe_id && !compact && !slot.leftover_of && (
          <p className="text-[10px] text-rf-ink-soft mt-auto">
            Serves {slot.servings}
          </p>
        )}
        {isCustom && !compact && (
          <p className="text-[10px] text-rf-ink-soft mt-auto italic">
            Custom meal
          </p>
        )}
      </div>
    </button>
  );
}


/* ---------- Picker sheet ---------- */

function RecipePickerSheet({ slot, weekStart, onClose, onSave, onClear, onMulti }) {
  const existing = slot.existing;
  const [multi, setMulti] = useState(false);
  const [picks, setPicks] = useState([]);
  const togglePick = (r) => setPicks((ps) => (ps.some((p) => p.recipe_id === r.recipe_id) ? ps.filter((p) => p.recipe_id !== r.recipe_id) : [...ps, r]));
  const isPicked = (r) => multi && picks.some((p) => p.recipe_id === r.recipe_id);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState({ inbox: [], cookbooks: [], community: [] });
  const [loading, setLoading] = useState(true);
  // Community-only filter chips — narrow the "From the community" list
  // without hiding the cook's own recipes. Empty means "no filter".
  const [commTag, setCommTag] = useState("");
  const [commMaxMins, setCommMaxMins] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(
    existing?.recipe_id ? {
      recipe_id: existing.recipe_id,
      title: existing.recipe_title,
      image: existing.recipe_image,
      serves: existing.recipe_serves || existing.servings,
    } : null,
  );
  // Freeform "custom meal" title — an escape hatch for entries that aren't
  // Recipe Filer recipes (takeout, dinner out, leftover roast). If the
  // slot came in with recipe_title set but no recipe_id, we preload it here
  // so re-opening the picker doesn't blank it.
  const [customTitle, setCustomTitle] = useState(
    !existing?.recipe_id && existing?.recipe_title ? existing.recipe_title : ""
  );
  const [servings, setServings] = useState(existing?.servings || 4);
  const [note, setNote] = useState(existing?.note || "");
  // Leftovers toggle — value = "{source_day}:{source_meal}" of the earlier
  // meal these leftovers came from. When set, this slot inherits its title
  // from the source and doesn't contribute to the shopping list.
  const [leftoverOf, setLeftoverOf] = useState(existing?.leftover_of || "");

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q, limit: 60 };
        if (commTag) params.community_tag = commTag;
        if (commMaxMins) params.max_minutes = commMaxMins;
        const { data } = await api.get("/meal-plan/recipe-drawer", { params });
        setDrawer({
          inbox: data?.inbox || [],
          cookbooks: data?.cookbooks || [],
          community: data?.community || [],
        });
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, commTag, commMaxMins]);

  // Group cookbook recipes by cookbook title (so a cook with multiple books
  // still sees which one each recipe lives in). Inbox and Community are
  // flat lists — they render as one section each.
  const cookbookGroups = useMemo(() => {
    const map = {};
    (drawer.cookbooks || []).forEach((r) => {
      const key = r.cookbook_title || "Uncategorised";
      (map[key] = map[key] || []).push(r);
    });
    return map;
  }, [drawer.cookbooks]);

  const totalResults =
    (drawer.inbox?.length || 0) +
    (drawer.cookbooks?.length || 0) +
    (drawer.community?.length || 0);

  const dayLabel = DAYS.find((x) => x.key === slot.day)?.label || slot.day;
  const mealLabel = MEALS.find((x) => x.key === slot.meal_type)?.label || slot.meal_type;

  const pickRecipe = (r) => {
    if (multi) { togglePick(r); return; }
    setSelectedRecipe(r);
    setServings(r.serves || 4);
  };

  const [saveState, setSaveState] = useState("idle");
  const save = async () => {
    setSaveState("saving");
    const ok = await onSave({
      recipe_id: selectedRecipe?.recipe_id || null,
      // Send custom_title ONLY when no recipe is picked — a picked recipe
      // supplies its own title.
      custom_title: !selectedRecipe && customTitle.trim() ? customTitle.trim() : "",
      servings,
      note,
      leftover_of: leftoverOf || "",
    });
    if (!ok) { setSaveState("idle"); return; }
    setSaveState("saved");
    setTimeout(onClose, 1000);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-rf-ink/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="planner-picker-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl bg-rf-cream rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
      >
        <div className="flex items-start justify-between p-4 sm:p-5 border-b border-rf-ink/10 bg-white">
          <div>
            <p className="overline">{dayLabel} · {mealLabel}</p>
            <h2 className="font-display font-black text-rf-ink text-xl">
              {selectedRecipe ? "Adjust this meal" : "Pick a recipe"}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-full hover:bg-rf-ink/10"
            data-testid="planner-picker-close"
          >
            <CloseIcon size={20} weight="bold" />
          </button>
        </div>

        {/* Selected recipe controls */}
        {selectedRecipe && (
          <div className="p-4 sm:p-5 bg-rf-amber/15 border-b border-rf-ink/10 flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {selectedRecipe.image ? (
                <img src={selectedRecipe.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center shrink-0">
                  <ForkKnife size={22} weight="duotone" className="text-rf-orange" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-display font-bold text-rf-ink truncate">{selectedRecipe.title}</p>
                <button
                  onClick={() => setSelectedRecipe(null)}
                  className="text-xs text-rf-orange hover:underline font-bold"
                  data-testid="planner-picker-change"
                >
                  Change recipe
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-rf-ink-soft">Serves</label>
              <div className="inline-flex items-center gap-1">
                <button
                  onClick={() => setServings((n) => Math.max(1, n - 1))}
                  className="btn-outline-ink !py-1 !px-2 text-sm"
                  aria-label="Decrease servings"
                >-</button>
                <input
                  value={servings}
                  onChange={(e) => {
                    const n = parseInt(e.target.value || "0", 10);
                    if (Number.isFinite(n)) setServings(Math.max(1, Math.min(99, n)));
                  }}
                  className="w-12 text-center input-rf !py-1"
                  data-testid="planner-picker-servings"
                />
                <button
                  onClick={() => setServings((n) => Math.min(99, n + 1))}
                  className="btn-outline-ink !py-1 !px-2 text-sm"
                  aria-label="Increase servings"
                >+</button>
              </div>
            </div>
          </div>
        )}

        {/* Note */}
        <div className="px-4 sm:px-5 pt-4">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (leftovers · takeout · date night)"
            className="input-rf text-sm"
            data-testid="planner-picker-note"
          />
        </div>

        {/* Leftovers marker — only meaningful once a recipe is picked
            (or a custom title is set). Skips the shopping list because
            the source meal already supplied its ingredients. */}
        {(selectedRecipe || customTitle.trim()) && (
          <div className="px-4 sm:px-5 pt-3">
            <label className="flex items-start gap-3 text-sm text-rf-ink">
              <input
                type="checkbox"
                data-testid="planner-picker-leftovers-toggle"
                checked={!!leftoverOf}
                onChange={(e) => setLeftoverOf(e.target.checked ? (leftoverOf || "prev-meal") : "")}
                className="mt-1 w-4 h-4 accent-rf-orange"
              />
              <div className="flex-1">
                <span className="font-bold">This is leftovers</span>
                <p className="text-xs text-rf-ink-soft">Skips the shopping list — the source meal already supplied the ingredients.</p>
              </div>
            </label>
          </div>
        )}

        {/* Search + list — only shown when no recipe selected, or when
            user clicks "Change recipe" (which clears selectedRecipe). */}
        {!selectedRecipe && (
          <>
            <div className="px-4 sm:px-5 pt-4">
              <label className="overline block mb-2">Not in Recipe Filer?</label>
              <input
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value.slice(0, 120))}
                placeholder="Type your own meal — e.g. Takeout curry, Sunday roast at Mum's"
                className="input-rf text-sm"
                data-testid="planner-picker-custom-title"
              />
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-rf-ink/10" />
                <span className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold">or pick a recipe</span>
                <div className="flex-1 h-px bg-rf-ink/10" />
              </div>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft pointer-events-none" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search your recipes or anything public…"
                  className="input-rf !pl-11 text-sm"
                  data-testid="planner-picker-search"
                />
              </div>
              <button type="button" onClick={() => { setMulti((m) => !m); setPicks([]); }} data-testid="planner-picker-multi-toggle"
                      className="mt-3 text-sm font-bold text-rf-orange hover:underline">
                {multi ? "Back to picking one recipe" : "Pick several recipes to fill the week"}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">
              {loading && <p className="text-center text-rf-ink-soft py-6">Loading recipes…</p>}
              {!loading && totalResults === 0 && (
                <p className="text-center text-rf-ink-soft py-6" data-testid="planner-picker-empty">
                  {q ? "No matches. Try another word." : "You don't have any recipes yet."}
                </p>
              )}

              {/* ---- Inbox ---- */}
              {!loading && drawer.inbox?.length > 0 && (
                <div className="mb-6" data-testid="picker-section-inbox">
                  <StickySectionHeader
                    label="From your Inbox"
                    hint="Freshly scanned & saved — not yet filed into a cookbook."
                    count={drawer.inbox.length}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {drawer.inbox.map((r) => (
                      <RecipeRow key={r.recipe_id} recipe={r} picked={isPicked(r)} onClick={() => pickRecipe(r)} />
                    ))}
                  </div>
                </div>
              )}

              {/* ---- Own cookbooks (grouped by book) ---- */}
              {!loading && drawer.cookbooks?.length > 0 && (
                <div className="mb-6" data-testid="picker-section-cookbooks">
                  <StickySectionHeader
                    label="From your cookbooks"
                    hint={Object.keys(cookbookGroups).length > 1
                      ? `Grouped by book — ${Object.keys(cookbookGroups).length} books total.`
                      : null}
                    count={drawer.cookbooks.length}
                  />
                  {Object.entries(cookbookGroups).map(([book, list]) => (
                    <div key={book} className="mb-4 last:mb-0">
                      <p className="overline mb-2">{book}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {list.map((r) => (
                          <RecipeRow key={r.recipe_id} recipe={r} picked={isPicked(r)} onClick={() => pickRecipe(r)} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ---- Community ---- */}
              {!loading && drawer.community?.length > 0 && (
                <div className="mb-2" data-testid="picker-section-community">
                  <StickySectionHeader
                    label="From the community"
                    hint="Public recipes shared by other Recipe Filer cooks."
                    count={drawer.community.length}
                  />
                  <CommunityFilterChips
                    tag={commTag}
                    maxMins={commMaxMins}
                    onTagChange={setCommTag}
                    onMaxMinsChange={setCommMaxMins}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {drawer.community.map((r) => (
                      <RecipeRow key={r.recipe_id} recipe={r} picked={isPicked(r)} onClick={() => pickRecipe(r)} />
                    ))}
                  </div>
                </div>
              )}
              {!loading && drawer.community?.length === 0 && (commTag || commMaxMins) && (
                <div className="mb-2" data-testid="picker-section-community-empty">
                  <StickySectionHeader
                    label="From the community"
                    hint="Public recipes shared by other Recipe Filer cooks."
                    count={0}
                  />
                  <CommunityFilterChips
                    tag={commTag}
                    maxMins={commMaxMins}
                    onTagChange={setCommTag}
                    onMaxMinsChange={setCommMaxMins}
                  />
                  <p className="text-center text-rf-ink-soft py-6 text-sm">
                    No community recipes match this filter — try a different one, or tap it again to clear.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Actions */}
        <div className="p-4 sm:p-5 border-t border-rf-ink/10 bg-white flex flex-wrap gap-2 justify-between">
          <div>
            {existing?.recipe_id || existing?.recipe_title || existing?.note ? (
              <button
                onClick={onClear}
                className="btn-outline-ink !py-2 !px-3 text-sm !border-red-400 !text-red-600 inline-flex items-center gap-2"
                data-testid="planner-picker-clear"
              >
                <Trash size={16} /> Clear slot
              </button>
            ) : <span />}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-outline-ink !py-2 !px-3 text-sm" data-testid="planner-picker-cancel">
              Cancel
            </button>
            {multi && (
              <button onClick={() => onMulti(picks)} disabled={!picks.length} data-testid="planner-picker-multi-next"
                      className="btn-orange !py-2 !px-4 text-sm disabled:opacity-50">
                {picks.length ? `Plan ${picks.length} recipe${picks.length === 1 ? "" : "s"} →` : "Tick recipes"}
              </button>
            )}
            <button
              onClick={save}
              disabled={(!selectedRecipe && !customTitle.trim() && !note.trim()) || saveState !== "idle"}
              aria-live="polite"
              className={`btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1.5 disabled:cursor-not-allowed ${multi ? "hidden" : ""} ${saveState !== "idle" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-600" : ""}`}
              data-testid="planner-picker-save"
              data-state={saveState}
            >
              {saveState === "saved" ? <><Check size={14} weight="bold" /> Saved</> : saveState === "saving" ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipeRow({ recipe, onClick, picked = false }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={picked}
      className={`flex items-center gap-3 p-2 rounded-xl border text-left transition min-w-0 ${picked ? "border-rf-orange bg-rf-amber/20 ring-2 ring-rf-orange" : "border-rf-ink/10 bg-white hover:border-rf-orange hover:shadow"}`}
      data-testid={`picker-recipe-${recipe.recipe_id}`}
    >
      {recipe.image ? (
        <img src={recipe.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-rf-amber/25 flex items-center justify-center shrink-0">
          <ForkKnife size={18} weight="duotone" className="text-rf-orange" />
        </div>
      )}
      <div className="min-w-0">
        <p className="font-display font-bold text-rf-ink truncate text-sm">{recipe.title}</p>
        <p className="text-xs text-rf-ink-soft">Serves {recipe.serves || 4}{recipe.is_own ? "" : " · public"}</p>
      </div>
      {picked && <Check size={16} weight="bold" className="ml-auto shrink-0 text-rf-orange" />}
    </button>
  );
}


/* ---------- Sticky section header for the recipe drawer ---------- */

function StickySectionHeader({ label, hint, count }) {
  return (
    <div
      className="sticky top-0 z-10 -mx-4 sm:-mx-5 px-4 sm:px-5 py-2 bg-rf-cream/95 backdrop-blur border-b border-rf-ink/10 mb-3"
      data-testid={`picker-header-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-display font-black text-rf-ink text-base">{label}</p>
        <span className="text-[10px] font-black uppercase tracking-wider text-rf-ink-soft bg-white px-2 py-0.5 rounded-full border border-rf-ink/10">
          {count}
        </span>
      </div>
      {hint && (
        <p className="text-[11px] text-rf-ink-soft italic leading-snug mt-0.5">{hint}</p>
      )}
    </div>
  );
}



/* ---------- Community filter chip row ---------- */

const COMMUNITY_TAG_CHIPS = [
  { key: "vegetarian",   label: "Vegetarian" },
  { key: "vegan",        label: "Vegan" },
  { key: "kid-friendly", label: "Kid-friendly" },
  { key: "gluten-free",  label: "Gluten-free" },
  { key: "dessert",      label: "Dessert" },
];
const COMMUNITY_TIME_CHIPS = [
  { mins: 20, label: "Under 20 min" },
  { mins: 30, label: "Under 30 min" },
  { mins: 60, label: "Under 1 hour" },
];

function CommunityFilterChips({ tag, maxMins, onTagChange, onMaxMinsChange }) {
  const chip = (active) =>
    `text-xs font-bold px-3 py-1.5 rounded-full border transition ${
      active
        ? "bg-rf-orange text-white border-rf-orange"
        : "bg-white text-rf-ink border-rf-ink/15 hover:border-rf-orange hover:text-rf-orange"
    }`;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="community-filter-row">
      <span className="text-[10px] font-black uppercase tracking-wider text-rf-ink-soft mr-1">Filter</span>
      {COMMUNITY_TAG_CHIPS.map((t) => {
        const active = tag === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onTagChange(active ? "" : t.key)}
            className={chip(active)}
            data-testid={`community-tag-${t.key}`}
          >
            {t.label}
          </button>
        );
      })}
      {COMMUNITY_TIME_CHIPS.map((t) => {
        const active = maxMins === t.mins;
        return (
          <button
            key={t.mins}
            onClick={() => onMaxMinsChange(active ? null : t.mins)}
            className={chip(active)}
            data-testid={`community-time-${t.mins}`}
          >
            {t.label}
          </button>
        );
      })}
      {(tag || maxMins) && (
        <button
          onClick={() => { onTagChange(""); onMaxMinsChange(null); }}
          className="text-xs font-bold text-rf-orange hover:underline ml-1"
          data-testid="community-filter-clear"
        >
          Clear
        </button>
      )}
    </div>
  );
}

